// Helper functions to match LGA names from Google Maps to our regional data structure
import { REGIONAL_DATA } from '../constants/regionalData';

/**
 * Normalize a string for comparison by removing common suffixes and converting to lowercase
 */
const normalizeString = (str) => {
  if (!str) return '';
  
  return str
    .toLowerCase()
    .trim()
    // Remove common LGA suffixes
    .replace(/\s+(city|council|shire|regional|municipality|aboriginal|town|district|area)$/gi, '')
    .replace(/\s+city\s+council$/gi, '')
    .replace(/\s+regional\s+council$/gi, '')
    .replace(/\s+shire\s+council$/gi, '')
    .replace(/\s+municipal\s+council$/gi, '')
    .trim();
};

/**
 * Find the best match for an LGA name in our regional data
 * Returns { state, greaterRegion, subRegion } or null if no match found
 */
export const findRegionByLGA = (lgaName, stateName = null) => {
  if (!lgaName) return null;
  
  const normalizedLGA = normalizeString(lgaName);
  
  // If we have a state name, search only in that state
  const statesToSearch = stateName && REGIONAL_DATA[stateName] 
    ? [stateName] 
    : Object.keys(REGIONAL_DATA);
  
  // Search through all states (or just the specified state)
  for (const state of statesToSearch) {
    const greaterRegions = REGIONAL_DATA[state];
    
    // Search through all greater regions in this state
    for (const greaterRegion of Object.keys(greaterRegions)) {
      const subRegions = greaterRegions[greaterRegion];
      
      // Search through all sub-regions
      for (const subRegion of subRegions) {
        const normalizedSubRegion = normalizeString(subRegion);
        
        // Check for exact match (after normalization)
        if (normalizedSubRegion === normalizedLGA) {
          return { state, greaterRegion, subRegion };
        }
        
        // Check if the LGA name contains the sub-region or vice versa
        if (normalizedLGA.includes(normalizedSubRegion) || normalizedSubRegion.includes(normalizedLGA)) {
          return { state, greaterRegion, subRegion };
        }
      }
    }
  }
  
  // If no match found, try a more lenient search (just check if words match)
  for (const state of statesToSearch) {
    const greaterRegions = REGIONAL_DATA[state];
    
    for (const greaterRegion of Object.keys(greaterRegions)) {
      const subRegions = greaterRegions[greaterRegion];
      
      for (const subRegion of subRegions) {
        const normalizedSubRegion = normalizeString(subRegion);
        
        // Split into words and check if key words match
        const lgaWords = normalizedLGA.split(/\s+/);
        const subRegionWords = normalizedSubRegion.split(/\s+/);
        
        // Check if any significant words match (ignore single-letter words)
        const significantLgaWords = lgaWords.filter(w => w.length > 2);
        const significantSubRegionWords = subRegionWords.filter(w => w.length > 2);
        
        if (significantLgaWords.length > 0 && significantSubRegionWords.length > 0) {
          const matchCount = significantLgaWords.filter(word => 
            significantSubRegionWords.some(srWord => srWord.includes(word) || word.includes(srWord))
          ).length;
          
          // If at least half the words match, consider it a match
          if (matchCount >= Math.ceil(significantLgaWords.length / 2)) {
            return { state, greaterRegion, subRegion };
          }
        }
      }
    }
  }
  
  return null;
};

/**
 * Map Google Maps state abbreviations to full state names
 */
const STATE_ABBREVIATION_MAP = {
  'NSW': 'New South Wales',
  'QLD': 'Queensland',
  'VIC': 'Victoria',
  'TAS': 'Tasmania',
  'SA': 'South Australia',
  'WA': 'Western Australia',
  'NT': 'Northern Territory',
  'ACT': 'Australian Capital Territory'
};

/**
 * Convert state abbreviation to full name
 */
export const expandStateName = (stateAbbr) => {
  if (!stateAbbr) return null;
  return STATE_ABBREVIATION_MAP[stateAbbr.toUpperCase()] || stateAbbr;
};

/**
 * Extract regional information from Google Maps address components
 */
export const extractRegionalInfo = (addressComponents) => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return null;
  }
  
  let lgaName = null;
  let stateAbbr = null;
  
  // Extract LGA (administrative_area_level_2) and state
  for (const component of addressComponents) {
    if (!component || !component.types) continue;
    
    for (const type of component.types) {
      if (type === 'administrative_area_level_2') {
        // This is the LGA/council area
        lgaName = component.longText || component.long_name;
      } else if (type === 'administrative_area_level_1') {
        // This is the state
        stateAbbr = component.shortText || component.short_name;
      }
    }
  }
  
  // Convert state abbreviation to full name
  const stateName = expandStateName(stateAbbr);
  
  // Find the matching region in our data
  const regionMatch = findRegionByLGA(lgaName, stateName);
  
  if (regionMatch) {
    return {
      state: regionMatch.state,
      greaterRegion: regionMatch.greaterRegion,
      subRegion: regionMatch.subRegion,
      lgaName: lgaName // Include the original LGA name for reference
    };
  }
  
  // If no match found, at least return the state
  if (stateName) {
    return {
      state: stateName,
      greaterRegion: null,
      subRegion: null,
      lgaName: lgaName
    };
  }
  
  return null;
};

